import type {
  OAuthLoginData,
  OAuthLoginUserinfo,
  OAuthLoginAccountInfo,
  OAuthLoginConnectionInfo,
  OAuthLoginBasic,
  PersistedLoginData,
} from "@/betby/api/types";
import LogUtil from "@/betby/utils/LogUtil";
import { LOGIN_BASIC_CONFIG } from "@/betby/utils/config";
import analyticsBridge from "@/betby/adapters/analytics/analyticsBridge";

export function buildTrackingSDKUpdatePayload(data: {
  user_id?: number | null;
  business_id?: number | null;
  userinfo?: OAuthLoginUserinfo | null;
  account_info?: OAuthLoginAccountInfo | null;
  connection_info?: OAuthLoginConnectionInfo | null;
  basic?: OAuthLoginBasic | null;
}): Record<string, unknown> {
  const {
    user_id,
    business_id,
    userinfo,
    account_info: accountInfo,
    connection_info: connectionInfo,
    basic,
  } = data;

  return {
    product_id:
      accountInfo?.product_id != null
        ? String(accountInfo.product_id)
        : undefined,
    business_id:
      business_id !== undefined && business_id !== null
        ? String(business_id)
        : undefined,
    user_id,
    sdk_id:
      accountInfo?.sdk_id != null ? String(accountInfo.sdk_id) : undefined,
    uip: connectionInfo?.ip,
    android_id: connectionInfo?.android_id,
    google_aid: connectionInfo?.google_aid,
    state: connectionInfo?.state,
    campaign: connectionInfo?.campaign,
    adgroup: accountInfo?.adgroup ?? "",
    adset: accountInfo?.adset ?? "",
    hot_version:
      basic?.hot_version != null
        ? String(basic.hot_version)
        : connectionInfo?.hot_version_code != null
          ? String(connectionInfo.hot_version_code)
          : undefined,
    sz_id: basic?.sz_id ?? "",
    abtest: basic?.ab_test ?? "",
    currency_code: userinfo?.currency_type,
    business_user_id: userinfo?.business_user_id,
    vip_level:
      accountInfo?.vip_level != null ? String(accountInfo.vip_level) : "",
    fd2_exchange_rate: userinfo?.exchange_rate,
    dz_exchange_rate: userinfo?.exchange_rate,
    media_source: connectionInfo?.media_source ?? "",
    attribution_id: connectionInfo?.trace_id ?? "",
    tp_device_id: connectionInfo?.sm_id ?? "",
    source_currency_code: userinfo?.currency_type ?? "",
    uuid: connectionInfo?.device_id ?? "",
    install_days: connectionInfo?.install_days ?? 0,
    os: connectionInfo?.os ?? "",
    os_language: connectionInfo?.language ?? "",
    country: connectionInfo?.initial_country ?? "",
    version_code: LOGIN_BASIC_CONFIG.sdk_version_code,
    version_name: LOGIN_BASIC_CONFIG.sdk_version_name,
  };
}

export function safeUpdateTrackingSDKWithLoginData(
  data: OAuthLoginData | null | undefined,
  isSuccess: boolean,
): void {
  try {
    if (typeof window === "undefined") return;

    const basePayload =
      data != null
        ? buildTrackingSDKUpdatePayload(data)
        : isSuccess
          ? null
          : buildTrackingSDKUpdatePayload({});

    if (basePayload == null) return;

    const finalPayload = isSuccess
      ? basePayload
      : {
          ...basePayload,
          business_id: "-1",
          user_id: -1,
          product_id: "-1",
          business_user_id: "-1",
          version_code: LOGIN_BASIC_CONFIG.sdk_version_code,
          version_name: LOGIN_BASIC_CONFIG.sdk_version_name,
          sdk_id: LOGIN_BASIC_CONFIG.SDK_ID,
        };

    analyticsBridge.update(finalPayload);
  } catch (error) {
    LogUtil.error("[AuthManager.login] 调用 TrackingSDK.update 失败", error);
  }
}

export function updateTrackingSDKFromSnapshot(
  snapshot?: {
    userId?: number | null;
    businessId?: number | null;
    loginData?: PersistedLoginData | null;
  } | null,
): void {
  try {
    if (typeof window === "undefined") return;

    const userId = snapshot?.userId ?? null;
    const businessId = snapshot?.businessId ?? null;
    const loginData = snapshot?.loginData ?? null;

    if (
      userId == null &&
      businessId == null &&
      !loginData?.userinfo &&
      !loginData?.account_info
    ) {
      return;
    }

    const payload = buildTrackingSDKUpdatePayload({
      user_id: userId,
      business_id: businessId,
      userinfo: loginData?.userinfo ?? null,
      account_info: loginData?.account_info ?? null,
      connection_info: loginData?.connection_info ?? null,
      basic: loginData?.basic ?? null,
    });

    analyticsBridge.update(payload);
    LogUtil.debug(
      "[auth] TrackingSDK.update 已从 store 恢复（刷新/已有 token 场景）",
    );
  } catch (error) {
    LogUtil.error("[auth] updateTrackingSDKFromStore 失败", error);
  }
}
